import { TaxReturnTOCItem } from '../model';

export interface PrintReportModalTOCItem extends Omit<TaxReturnTOCItem, 'children'> {
    nodeKey: string;
    children?: PrintReportModalTOCItem[] | null;
}

export type TaxReturnTOCItemWithNodeKey = TaxReturnTOCItem & {
    nodeKey?: string;
};

export interface TaxReturnTOCItemWithRequiredNodeKey extends TaxReturnTOCItem {
    nodeKey: string;
}

interface BuildPrintReportModalNodeKeyParams {
    sourceItem: TaxReturnTOCItem;
    parentNodeKey: string | null;
    sourceIndex: number;
}

export const buildPrintReportModalNodeKey = ({ sourceItem, parentNodeKey, sourceIndex }: BuildPrintReportModalNodeKeyParams): string => {
    const parentNodeKeySegment: string = parentNodeKey ?? 'root';
    const explicitIndex: number | null | undefined = sourceItem.index;
    const hasExplicitIndex: boolean = explicitIndex !== null && explicitIndex !== undefined;

    let nodeIndex: number = sourceIndex;
    if (hasExplicitIndex) {
        nodeIndex = explicitIndex ?? sourceIndex;
    }

    const formPageName: string | null | undefined = sourceItem.taxReturnTOCKey?.formPageName;
    const title: string | null | undefined = sourceItem.title;
    const id: string | null | undefined = sourceItem.id;

    const discriminatorParts: string[] = [];
    if (formPageName && formPageName.length > 0) {
        discriminatorParts.push(formPageName);
    }
    if (title && title.length > 0) {
        discriminatorParts.push(title);
    }
    if (id) {
        discriminatorParts.push(id);
    }
    let discriminator: string = discriminatorParts.join('::');
    if (!discriminator) {
        discriminator = `item-${sourceIndex}`;
    }

    return `${parentNodeKeySegment}::${nodeIndex}::${discriminator}`;
};

export const findPrintReportModalNodeKeyForSourceItem = (
    sourceItems: TaxReturnTOCItem[] | null | undefined,
    targetItem: TaxReturnTOCItem
): string | undefined => {
    if (!sourceItems || sourceItems.length === 0) {
        return undefined;
    }

    const visitItems = (items: TaxReturnTOCItem[], parentNodeKey: string | null): string | undefined => {
        for (const [sourceIndex, sourceItem] of items.entries()) {
            const nodeKey: string = buildPrintReportModalNodeKey({
                sourceItem,
                parentNodeKey,
                sourceIndex,
            });

            if (sourceItem === targetItem) {
                return nodeKey;
            }

            const childItems: TaxReturnTOCItem[] = sourceItem.children ?? [];
            const childMatch: string | undefined = visitItems(childItems, nodeKey);

            if (childMatch) {
                return childMatch;
            }
        }

        return undefined;
    };

    return visitItems(sourceItems, null);
};

export const getPrintReportModalNodeKey = (item: TaxReturnTOCItemWithNodeKey): string | undefined => {
    const nodeKey: string | undefined = item.nodeKey;

    if (nodeKey) {
        return nodeKey;
    }

    return item.id;
};

export const buildPrintReportModalTOCItems = (
    sourceItems: TaxReturnTOCItem[] | null | undefined,
    parentNodeKey: string | null = null
): PrintReportModalTOCItem[] => {
    if (!sourceItems || sourceItems.length === 0) {
        return [];
    }

    return sourceItems.map((sourceItem: TaxReturnTOCItem, sourceIndex: number): PrintReportModalTOCItem => {
        const nodeKey: string = buildPrintReportModalNodeKey({
            sourceItem,
            parentNodeKey,
            sourceIndex,
        });
        const sourceChildItems: TaxReturnTOCItem[] | null | undefined = sourceItem.children;
        const hasChildItems: boolean = Boolean(sourceChildItems && sourceChildItems.length > 0);

        let childItems: PrintReportModalTOCItem[] | undefined = undefined;
        if (hasChildItems && sourceChildItems) {
            childItems = buildPrintReportModalTOCItems(sourceChildItems, nodeKey);
        }

        return {
            ...sourceItem,
            nodeKey,
            children: childItems,
        };
    });
};

export const traversePrintReportModalTOCItem = (
    tocItem: PrintReportModalTOCItem,
    callback: (item: PrintReportModalTOCItem) => void
): void => {
    callback(tocItem);

    const childItems: PrintReportModalTOCItem[] = tocItem.children ?? [];
    for (const childItem of childItems) {
        traversePrintReportModalTOCItem(childItem, callback);
    }
};
